import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
} from "reactstrap";
import ReactDatetime from "components/CustomDatePicker";
import Select from "components/Select";
import DollarInput from "components/DollarInput";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import { formatPriceWithDollar, toNumber, formatPercentageInput, displayPercentage } from "utils/currency";
import { formatDateForAPI, formatDateForPicker, getCurrentDateForAPI } from "utils/dateUtils";
import { formatPhoneNumber } from "utils/phoneUtils";
import { useCreatePortfolio } from "api/portfolios/service";
import { useGetCountries } from "api/countries/service";
import { useGetChannels } from "api/channels/service";
import { getSelectStyles } from "../../../utils/selectStyles";
import { useNotification } from "../../../contexts/NotificationContext";
import { useTranslation } from "react-i18next";

const AddPortfolios = ({ activeColor = "secondary" }) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);

  const navigate = useNavigate();
  const createMutation = useCreatePortfolio();

  // Reference data
  const { data: countriesDataOriginal } = useGetCountries();
  const { data: investors } = useGetChannels("investor");
  const { data: bookkeepingCompany } = useGetChannels("bookkeeping_company");

  console.log("bookkeepingCompany", bookkeepingCompany);
  const countriesData = countriesDataOriginal?.data || [];

  const [isLightTheme, setIsLightTheme] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.body.classList.contains('white-content'));
    };
    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  const customSelectStyles = useMemo(() => getSelectStyles(activeColorStyle, isLightTheme), [activeColorStyle, isLightTheme]);

  const [formData, setFormData] = useState({
    // Portfolio Detail
    name: "",
    description: "",
    type: "",
    date: getCurrentDateForAPI(),
    active: true,
    in_house: false,
    servicing_company_name: "",
    stock_namer_as_loan_number: false,
    loan_number_prefix: "",
    next_loan_number: "",
    include_down_past: false,
    include_misc_past: false,
    include_defer_down_past: false,
    include_insurance_past: false,
    include_regular_past: false,
    include_other_side_past: false,
    tolorance: formatPriceWithDollar(0),
    tolorance_percent: "0",

    // Loan Investor Participation
    investors: [
      { investor: "", ownership: "0" },
      { investor: "", ownership: "0" },
      { investor: "", ownership: "0" },
      { investor: "", ownership: "0" },
      { investor: "", ownership: "0" },
    ],

    // Letter Generation Settings
    letter_generation_settings: [
      { name: "Auto Reminders/Loan Bills", send: false, notice_type: "", days_before: 0 },
      { name: "10 Day Past Due Notice", send: false, notice_type: "", days_before: 0 },
      { name: "15 Day Past Due Notice", send: false, notice_type: "", days_before: 0 },
      { name: "30 Day Past Due Notice", send: false, notice_type: "", days_before: 0 },
      { name: "60 Day Past Due Notice", send: false, notice_type: "", days_before: 0 },
      { name: "90 Day Past Due Notice", send: false, notice_type: "", days_before: 0 },
      { name: "120 Day Past Due Notice", send: false, notice_type: "", days_before: 0 },
      { name: "Insurance Reminder Notices", send: false, notice_type: "", days_before: 0 },
      { name: "Expired Insurance Notices", send: false, notice_type: "", days_before: 0 },
    ],

    // Address Section
    address_name: "",
    address_text_name: "",
    address_1: "",
    address_2: "",
    zip: "",
    country: "",
    city: "",
    state: "",
    phone: "",
    fax: "",
    website: "",
    receipt_message: "",
  });

  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);

  const typeOptions = useMemo(() => [
    { value: "Flow Pool", label: t('addPortfolios.flowPool') },
    { value: "Bulk Sale", label: t('addPortfolios.bulkSale') },
    { value: "Spec Pool", label: t('addPortfolios.specPool') },
  ], [t]);

  const yesNoOptions = useMemo(() => [
    { value: false, label: t('common.no') },
    { value: true, label: t('common.yes') },
  ], [t]);


  const countryOptions = countriesData?.map(c => ({ value: c.id, label: c.name }));
  const selectedCountry = useMemo(() => countriesData?.find(c => String(c.id) === String(formData.country)), [countriesData, formData.country]);
  const derivedStateOptions = selectedCountry?.states || [];
  const hasStates = Array.isArray(derivedStateOptions) && derivedStateOptions.length > 0;
  // City is a free text input (like DealApplicantTab)
  const handleBasicChange = (e) => {
    const { name, value, type, checked } = e.target;
    let finalValue = value;
    if (name === "phone" || name === "fax") {
      finalValue = formatPhoneNumber(value);
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : finalValue,
      ...(name === 'country' ? { state: '' } : {})
    }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handlePercentChange = (name, value) => {
    const formatted = formatPercentageInput(value);
    setFormData(prev => ({ ...prev, [name]: formatted }));
  };

  const handleInvestorChange = (index, investorId) => {
    const newInvestors = [...formData.investors];
    newInvestors[index] = { ...newInvestors[index], investor: investorId || "" };
    setFormData(prev => ({ ...prev, investors: newInvestors }));
    if (errors.investors) setErrors(prev => ({ ...prev, investors: "" }));
  };

  const handleOwnershipChange = (index, value) => {
    const formatted = formatPercentageInput(value);
    const newInvestors = [...formData.investors];
    newInvestors[index] = { ...newInvestors[index], ownership: formatted };
    setFormData(prev => ({ ...prev, investors: newInvestors }));
  };

  const handleLetterSettingChange = (index, field, value) => {
    const newSettings = [...formData.letter_generation_settings];
    newSettings[index] = { ...newSettings[index], [field]: value };
    setFormData(prev => ({ ...prev, letter_generation_settings: newSettings }));
  };

  const validateForm = () => {
    const newErrors = {};
    const missingFields = [];
    
    if (!formData.name || !formData.name.trim()) {
      newErrors.name = t('addPortfolios.portfolioNameRequired');
      missingFields.push(t('addPortfolios.portfolioName'));
    }
    if (!formData.type) {
      newErrors.type = t('addPortfolios.typeRequired');
      missingFields.push(t('addPortfolios.type'));
    }
    // Newly required fields per request
    if (!formData.description || !formData.description.trim()) {
      newErrors.description = t('addPortfolios.portfolioDescriptionRequired');
      missingFields.push(t('addPortfolios.description'));
    }
    if (!formData.servicing_company_name || !formData.servicing_company_name.trim()) {
      newErrors.servicing_company_name = t('addPortfolios.servicingCompanyNameRequired');
      missingFields.push(t('addPortfolios.servicingCompanyName'));
    }
    if (!formData.loan_number_prefix || !formData.loan_number_prefix.trim()) {
      newErrors.loan_number_prefix = t('addPortfolios.loanNumberPrefixRequired');
      missingFields.push(t('addPortfolios.loanNumberPrefix'));
    }
    if (!formData.next_loan_number || !formData.next_loan_number.toString().trim()) {
      newErrors.next_loan_number = t('addPortfolios.nextLoanNumberRequired');
      missingFields.push(t('addPortfolios.nextLoanNumber'));
    }
    // Investor validation - at least one investor is required
    const hasInvestor = formData.investors.some(inv => inv.investor);
    if (!hasInvestor) {
      newErrors.investors = t('addPortfolios.atLeastOneInvestorRequired');
      missingFields.push(t('addPortfolios.investor'));
    }
    // Address required fields
    if (!formData.address_name || !formData.address_name.trim()) {
      newErrors.address_name = t('addPortfolios.nameRequired');
      missingFields.push(t('addPortfolios.addressName'));
    }
    if (!formData.address_1 || !formData.address_1.trim()) {
      newErrors.address_1 = t('addPortfolios.address1Required');
      missingFields.push(t('addPortfolios.address1'));
    }
    if (!formData.zip || !formData.zip.toString().trim()) {
      newErrors.zip = t('addPortfolios.zipRequired');
      missingFields.push(t('addPortfolios.zip'));
    }
    if (!formData.city) {
      newErrors.city = t('addPortfolios.cityRequired');
      missingFields.push(t('addPortfolios.city'));
    }
    if (!formData.state) {
      newErrors.state = t('addPortfolios.stateRequired');
      missingFields.push(t('addPortfolios.state'));
    }
    if (!formData.phone || !formData.phone.trim()) {
      newErrors.phone = t('addPortfolios.phoneRequired');
      missingFields.push(t('addPortfolios.phone'));
    }

    setErrors(newErrors);
    
    // Show notification if there are errors
    if (Object.keys(newErrors).length > 0) {
      const fieldsList = missingFields.join(', ');
      showNotification(
        'danger',
        `${t('addPortfolios.requiredFieldsMissing')}: ${fieldsList}`
      );
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      name: formData.name,
      description: formData.description,
      type: formData.type || null,
      date: formatDateForAPI(formData.date),
      active: !!formData.active,
      in_house: !!formData.in_house,
      servicing_company_name: formData.servicing_company_name || "",
      stock_namer_as_loan_number: !!formData.stock_namer_as_loan_number,
      loan_number_prefix: formData.loan_number_prefix || "",
      next_loan_number: formData.next_loan_number || "",
      include_down_past: !!formData.include_down_past,
      include_misc_past: !!formData.include_misc_past,
      include_defer_down_past: !!formData.include_defer_down_past,
      include_insurance_past: !!formData.include_insurance_past,
      include_regular_past: !!formData.include_regular_past,
      include_other_side_past: !!formData.include_other_side_past,
      bookkeeping_company: formData.bookkeeping_company || null,
      tolorance: toNumber(formData.tolorance),
      tolorance_percent: toNumber(formData.tolorance_percent),
      loan_investor: formData.investors
        .filter(inv => inv.investor)
        .map(inv => ({
          investor: inv.investor,
          ownership: toNumber(inv.ownership),
        })),

      letter_generation_settings: formData.letter_generation_settings.map(setting => ({
        name: setting.name,
        send: setting.send,
        notice_type: setting.notice_type || "",
        days_before: setting.days_before || 0,
      })),

      address_name: formData.address_name || "",
      address_text_name: formData.address_text_name || "",
      address_1: formData.address_1 || "",
      address_2: formData.address_2 || "",
      zip: formData.zip || "",
      country: formData.country || null,
      city: formData.city || null,
      state: formData.state || null,
      phone: formData.phone || "",
      fax: formData.fax || "",
      website: formData.website || "",
      receipt_message: formData.receipt_message || "",
    };

    createMutation.mutate(payload);
  };

  const handleCancel = () => {
    navigate('/portfolios');
  };

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <CardTitle tag="h4" style={{ margin: 0 }}>
                  <i className="tim-icons icon-briefcase-24" style={{ marginRight: '10px' }} />
                  {t('addPortfolios.addNewPortfolio')}
                </CardTitle>
                <Button
                  color="secondary"
                  size="sm"
                  onClick={handleCancel}
                  disabled={createMutation.isPending}
                >
                  <i className="tim-icons icon-simple-remove" style={{ marginRight: '5px' }} />
                  {t('common.cancel')}
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md="4">
                    <div className="card" style={{ backgroundColor: activeColorStyle.backgroundColor }}>
                      <div className="card-header font-weight-bold text-uppercase">{t('addPortfolios.portfolioDetail')}</div>
                      <div className="card-body">
                        <FormGroup>
                          <Label>{t('addPortfolios.whatBookkeepingCompanyOwnsThisPortfolio')}</Label>
                          <Select
                            styles={customSelectStyles}
                            className={`react-select`}
                            placeholder={t('addPortfolios.selectBookkeepingCompany')}
                            name="type"
                            isSearchable
                            closeMenuOnSelect={true}
                            value={bookkeepingCompany?.results?.find(bc => bc.id === formData.bookkeeping_company) ? { value: bookkeepingCompany?.results?.find(bc => bc.id === formData.bookkeeping_company).id, label: bookkeepingCompany?.results?.find(bc => bc.id === formData.bookkeeping_company).name } : null}
                            onChange={(value) => {
                              setFormData(prev => ({ ...prev, bookkeeping_company: value?.value || "" }));
                            }}
                            options={bookkeepingCompany?.results?.map(bc => ({ value: bc.id, label: bc.name }))}
                          />

                        </FormGroup>
                        <FormGroup>
                          <Label>{t('addPortfolios.portfolioName')} <span style={{ color: 'red' }}>*</span></Label>
                          <Input
                            name="name"
                            type="text"
                            value={formData.name}
                            onChange={handleBasicChange}
                            className={errors.name ? 'is-invalid' : ''}
                            placeholder={t('addPortfolios.enterPortfolioName')}
                          />

                        </FormGroup>
                        <FormGroup>
                          <Label>{t('addPortfolios.portfolioDescription')} <span style={{ color: 'red' }}>*</span></Label>
                          <Input
                            name="description"
                            type="textarea"
                            rows="3"
                            value={formData.description}
                            onChange={handleBasicChange}
                            placeholder={t('addPortfolios.writeDescription')}
                            className={errors.description ? 'is-invalid' : ''}
                          />

                        </FormGroup>
                        <Row>
                          <Col md="6">
                            <FormGroup>
                              <Label>{t('addPortfolios.portfolioType')} <span style={{ color: 'red' }}>*</span></Label>
                              <Select
                                styles={customSelectStyles}
                                className={`react-select${errors.type ? " is-invalid" : ""}`}
                                placeholder={t('common.select')}
                                name="type"
                                isSearchable
                                closeMenuOnSelect={true}
                                value={formData.type ? { value: formData.type, label: typeOptions.find(opt => opt.value === formData.type)?.label || formData.type } : null}
                                onChange={(value) => {
                                  setFormData(prev => ({ ...prev, type: value?.value || "" }));
                                  if (errors.type) setErrors(prev => ({ ...prev, type: "" }));
                                }}
                                options={typeOptions}
                              />

                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label>{t('addPortfolios.originationDate')}</Label>
                              <ReactDatetime
                                inputProps={{
                                  className: "form-control",
                                  placeholder: t('addPortfolios.selectDate'),
                                  style: { borderRadius: 0 }
                                }}
                                value={formData.date ? formatDateForPicker(formData.date) : formData.date}
                                onChange={(date) => setFormData(prev => ({ ...prev, date }))}
                                dateFormat="MM-DD-YYYY"
                                timeFormat={false}
                                closeOnSelect={true}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label>{t('addPortfolios.portfolioActivelyUsed')}</Label>
                              <Select
                                styles={customSelectStyles}
                                className="react-select"
                                placeholder={t('common.select')}
                                name="active"
                                isSearchable={false}
                                closeMenuOnSelect={true}
                                value={yesNoOptions.find(opt => opt.value === formData.active)}
                                onChange={(value) => setFormData(prev => ({ ...prev, active: value?.value ?? true }))}
                                options={yesNoOptions}
                              />
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label>{t('addPortfolios.portfolioServicedInHouse')}</Label>
                              <Select
                                styles={customSelectStyles}
                                className="react-select"
                                placeholder={t('common.select')}
                                name="in_house"
                                isSearchable={false}
                                closeMenuOnSelect={true}
                                value={yesNoOptions.find(opt => opt.value === formData.in_house)}
                                onChange={(value) => setFormData(prev => ({ ...prev, in_house: value?.value ?? false }))}
                                options={yesNoOptions}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <FormGroup>
                          <Label>{t('addPortfolios.servicingCompanyName')} <span style={{ color: 'red' }}>*</span></Label>
                          <Input name="servicing_company_name" type="text" value={formData.servicing_company_name} onChange={handleBasicChange} className={errors.servicing_company_name ? 'is-invalid' : ''} />

                        </FormGroup>
                        <Row>
                          <Col md="12">
                            <FormGroup>
                              <Label>{t('addPortfolios.useStocknumberAsLoanNumber')}</Label>
                              <Select
                                styles={customSelectStyles}
                                className="react-select"
                                placeholder={t('common.select')}
                                name="stock_namer_as_loan_number"
                                isSearchable={false}
                                closeMenuOnSelect={true}
                                value={yesNoOptions.find(opt => opt.value === formData.stock_namer_as_loan_number)}
                                onChange={(value) => setFormData(prev => ({ ...prev, stock_namer_as_loan_number: value?.value ?? false }))}
                                options={yesNoOptions}
                              />
                            </FormGroup>
                          </Col>

                        </Row>
                        <Row>
                          <Col md="6">
                            <FormGroup>
                              <Label>{t('addPortfolios.nextLoanNumber')} <span style={{ color: 'red' }}>*</span></Label>
                              <Input name="next_loan_number" type="text" value={formData.next_loan_number} onChange={handleBasicChange} className={errors.next_loan_number ? 'is-invalid' : ''} />

                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label>{t('addPortfolios.loanNumberPrefix')} <span style={{ color: 'red' }}>*</span></Label>
                              <Input name="loan_number_prefix" type="text" value={formData.loan_number_prefix} onChange={handleBasicChange} placeholder={t('addPortfolios.loanNumberPrefixExample')} className={errors.loan_number_prefix ? 'is-invalid' : ''} />

                            </FormGroup>
                          </Col>
                        </Row>


                      </div>
                    </div>
                  </Col>


                  {/* Address on ACCT Collection Letters */}
                  <Col md="4">
                    <div className="card" style={{ backgroundColor: activeColorStyle.backgroundColor }}>
                      <div className="card-header font-weight-bold text-uppercase">{t('addPortfolios.addressOnAcctCollectionLetters')}</div>
                      <div className="card-body">
                        <Row>
                          <Col md="12">
                            <FormGroup>
                              <Label>{t('addPortfolios.name')} <span style={{ color: 'red' }}>*</span></Label>
                              <Input name="address_name" type="text" value={formData.address_name} onChange={handleBasicChange} className={errors.address_name ? 'is-invalid' : ''} />

                            </FormGroup>
                          </Col>
                          <Col md="12">
                            <FormGroup>
                              <Label>{t('addPortfolios.textName')}</Label>
                              <Input name="address_text_name" type="text" maxLength={12} value={formData.address_text_name} onChange={handleBasicChange} />
                            </FormGroup>
                          </Col>
                          <Col md="12">
                            <FormGroup>
                              <Label>{t('addPortfolios.address1')} <span style={{ color: 'red' }}>*</span></Label>
                              <Input name="address_1" type="text" value={formData.address_1} onChange={handleBasicChange} className={errors.address_1 ? 'is-invalid' : ''} />

                            </FormGroup>
                          </Col>
                          <Col md="12">
                            <FormGroup>
                              <Label>{t('addPortfolios.address2')}</Label>
                              <Input name="address_2" type="text" value={formData.address_2} onChange={handleBasicChange} />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md="4">
                            <FormGroup>
                              <Label>{t('addPortfolios.country')}</Label>
                              <Select
                                styles={customSelectStyles}
                                className="react-select"
                                placeholder={t('common.select')}
                                name="country"
                                isSearchable
                                closeMenuOnSelect={true}
                                value={formData.country ? { value: formData.country, label: countriesData?.find(c => String(c.id) === String(formData.country))?.name } : null}
                                onChange={(value) => {
                                  setFormData(prev => ({ ...prev, country: value?.value || "", state: "" }));
                                }}
                                options={countryOptions}
                                isClearable
                              />
                            </FormGroup>

                          </Col>
                          <Col md="4">
                            <FormGroup>
                              <Label>{t('addPortfolios.city')} <span style={{ color: 'red' }}>*</span></Label>
                              <Input name="city" type="text" value={formData.city} onChange={handleBasicChange} className={errors.city ? 'is-invalid' : ''} placeholder={t('addPortfolios.enterCity')} />

                            </FormGroup>
                          </Col>
                          <Col md="4">
                            <FormGroup>
                              <Label>{t('addPortfolios.state')} <span style={{ color: 'red' }}>*</span></Label>
                              <Select
                                styles={customSelectStyles}
                                className={`react-select${errors.state ? " is-invalid" : ""}`}
                                placeholder={!formData.country ? t('addPortfolios.selectCountryFirst') : (hasStates ? t('addPortfolios.selectState') : t('addPortfolios.noStatesForSelectedCountry'))}
                                name="state"
                                isSearchable
                                closeMenuOnSelect={true}
                                value={formData.state ? { value: formData.state, label: derivedStateOptions?.find(s => String(s.id) === String(formData.state))?.name } : null}
                                onChange={(value) => {
                                  setFormData(prev => ({ ...prev, state: value?.value || "" }));
                                  if (errors.state) setErrors(prev => ({ ...prev, state: "" }));
                                }}
                                options={hasStates ? derivedStateOptions.map(s => ({ value: String(s.id), label: s.name })) : []}
                                isDisabled={!hasStates}
                              />

                            </FormGroup>
                          </Col>
                        </Row>
                        <FormGroup>
                          <Label>{t('addPortfolios.zip')} <span style={{ color: 'red' }}>*</span></Label>
                          <Input name="zip" type="text" value={formData.zip} onChange={handleBasicChange} className={errors.zip ? 'is-invalid' : ''} />

                        </FormGroup>
                        <Row>
                          <Col md="6">
                            <FormGroup>
                              <Label>{t('addPortfolios.phone')} <span style={{ color: 'red' }}>*</span></Label>
                              <Input name="phone" type="text" value={formData.phone} onChange={handleBasicChange} className={errors.phone ? 'is-invalid' : ''} />

                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label>{t('addPortfolios.fax')}</Label>
                              <Input name="fax" type="text" value={formData.fax} onChange={handleBasicChange} />
                            </FormGroup>
                          </Col>
                        </Row>
                        <FormGroup>
                          <Label>{t('addPortfolios.website')}</Label>
                          <Input name="website" type="text" value={formData.website} onChange={handleBasicChange} />
                        </FormGroup>
                        <FormGroup>
                          <Label>{t('addPortfolios.pmtReceiptMessage')}</Label>
                          <Input name="receipt_message" type="textarea" rows="3" maxLength={150} value={formData.receipt_message} onChange={handleBasicChange} />
                        </FormGroup>
                      </div>
                    </div>
                  </Col>

                  {/* Right column placeholder to mirror layout */}
                  <Col md="4">

                    <div className="card" style={{ backgroundColor: activeColorStyle.backgroundColor }}>
                      <div className="card-header font-weight-bold text-uppercase">{t('addPortfolios.loanInvestorParticipation')} <span style={{ color: 'red' }}>*</span></div>
                      <div className="card-body">
                        <div className="text-muted mb-3" style={{ fontSize: 12 }}>{t('addPortfolios.percentagesMustAddUpTo100')}</div>
                        {errors.investors && <div className="text-danger mb-3" style={{ fontSize: 12 }}>{errors.investors}</div>}
                        {formData.investors.map((inv, index) => (
                          <Row key={index} >
                            <Col md="6">
                              <FormGroup>
                                <Label style={{ fontSize: 12 }}>{t('addPortfolios.investor')}</Label>
                                <Select
                                  styles={customSelectStyles}
                                  className="react-select"
                                  placeholder={t('common.select')}
                                  name={`investor_${index}`}
                                  isSearchable
                                  closeMenuOnSelect={true}
                                  value={inv.investor ? { value: inv.investor, label: investors?.results?.find((i) => i.id === inv.investor)?.name } : null}
                                  onChange={(value) => handleInvestorChange(index, value?.value)}
                                  options={investors?.results?.map((i) => ({ value: i.id, label: i.name }))}
                                  isClearable
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6">
                              <FormGroup>
                                <Label style={{ fontSize: 12 }}>{t('addPortfolios.ownershipPercent')}</Label>
                                <Input
                                  type="text"
                                  value={displayPercentage(inv.ownership, focusedField === `ownership_${index}`)}
                                  onChange={(e) => handleOwnershipChange(index, e.target.value)}
                                  onFocus={() => setFocusedField(`ownership_${index}`)}
                                  onBlur={() => setFocusedField(null)}
                                  style={{ textAlign: 'right' }}
                                  placeholder="0%"
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                        ))}
                      </div>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <div className="card" style={{ backgroundColor: activeColorStyle.backgroundColor }}>
                    <div className="card-header font-weight-bold text-uppercase">{t('addPortfolios.configureHowAccountPastDuesAreMonitored')}</div>
                    <div className="card-body">
                      <Row>
                        <Col md="4">
                          <FormGroup>
                            <Label>{t('addPortfolios.includeDownPmtsInPastDues')}</Label>
                            <Select
                              styles={customSelectStyles}
                              className="react-select"
                              placeholder={t('common.select')}
                              name="include_down_past"
                              isSearchable={false}
                              closeMenuOnSelect={true}
                              value={yesNoOptions.find(opt => opt.value === formData.include_down_past)}
                              onChange={(value) => setFormData(prev => ({ ...prev, include_down_past: value?.value ?? false }))}
                              options={yesNoOptions}
                            />
                          </FormGroup>
                        </Col>
                        <Col md="4">
                          <FormGroup>
                            <Label>{t('addPortfolios.includeMiscPmtsInPstDue')}</Label>
                            <Select
                              styles={customSelectStyles}
                              className="react-select"
                              placeholder={t('common.select')}
                              name="include_misc_past"
                              isSearchable={false}
                              closeMenuOnSelect={true}
                              value={yesNoOptions.find(opt => opt.value === formData.include_misc_past)}
                              onChange={(value) => setFormData(prev => ({ ...prev, include_misc_past: value?.value ?? false }))}
                              options={yesNoOptions}
                            />
                          </FormGroup>
                        </Col>
                        <Col md="4">
                          <FormGroup>
                            <Label>{t('addPortfolios.includeDeferDownPmtsInPstDue')}</Label>
                            <Select
                              styles={customSelectStyles}
                              className="react-select"
                              placeholder={t('common.select')}
                              name="include_defer_down_past"
                              isSearchable={false}
                              closeMenuOnSelect={true}
                              value={yesNoOptions.find(opt => opt.value === formData.include_defer_down_past)}
                              onChange={(value) => setFormData(prev => ({ ...prev, include_defer_down_past: value?.value ?? false }))}
                              options={yesNoOptions}
                            />
                          </FormGroup>
                        </Col>
                        <Col md="4">
                          <FormGroup>
                            <Label>{t('addPortfolios.includeInsurancePmtsInPstDue')}</Label>
                            <Select
                              styles={customSelectStyles}
                              className="react-select"
                              placeholder={t('common.select')}
                              name="include_insurance_past"
                              isSearchable={false}
                              closeMenuOnSelect={true}
                              value={yesNoOptions.find(opt => opt.value === formData.include_insurance_past)}
                              onChange={(value) => setFormData(prev => ({ ...prev, include_insurance_past: value?.value ?? false }))}
                              options={yesNoOptions}
                            />
                          </FormGroup>
                        </Col>
                        <Col md="4">
                          <FormGroup>
                            <Label>{t('addPortfolios.includeRegularPmtsInPstDue')}</Label>
                            <Select
                              styles={customSelectStyles}
                              className="react-select"
                              placeholder={t('common.select')}
                              name="include_regular_past"
                              isSearchable={false}
                              closeMenuOnSelect={true}
                              value={yesNoOptions.find(opt => opt.value === formData.include_regular_past)}
                              onChange={(value) => setFormData(prev => ({ ...prev, include_regular_past: value?.value ?? false }))}
                              options={yesNoOptions}
                            />
                          </FormGroup>
                        </Col>
                        <Col md="4">
                          <FormGroup>
                            <Label>{t('addPortfolios.incOtherSideNotePmtsInPstDue')}</Label>
                            <Select
                              styles={customSelectStyles}
                              className="react-select"
                              placeholder={t('common.select')}
                              name="include_other_side_past"
                              isSearchable={false}
                              closeMenuOnSelect={true}
                              value={yesNoOptions.find(opt => opt.value === formData.include_other_side_past)}
                              onChange={(value) => setFormData(prev => ({ ...prev, include_other_side_past: value?.value ?? false }))}
                              options={yesNoOptions}
                            />
                          </FormGroup>
                        </Col>
                        <Col md="6">
                          <FormGroup>
                            <Label>{t('addPortfolios.toleranceAmount')}</Label>
                            <DollarInput
                              name="tolorance"
                              value={formData.tolorance}
                              onChange={(value) => setFormData(prev => ({ ...prev, tolorance: value }))}
                            />
                          </FormGroup>
                        </Col>
                        <Col md="6">
                          <FormGroup>
                            <Label>{t('addPortfolios.tolerancePercent')}</Label>
                            <Input
                              name="tolorance_percent"
                              type="text"
                              value={displayPercentage(formData.tolorance_percent, focusedField === 'tolorance_percent')}
                              onChange={(e) => handlePercentChange('tolorance_percent', e.target.value)}
                              onFocus={() => setFocusedField('tolorance_percent')}
                              onBlur={() => setFocusedField(null)}
                              style={{ textAlign: 'right' }}
                              placeholder="0%"
                            />
                          </FormGroup>
                        </Col>

                      </Row>
                    </div>
                  </div>
                </Row>

                {/* Automatic Letter Generation Settings */}
                <Row>
                  <div className="card" style={{ backgroundColor: activeColorStyle.backgroundColor }}>
                    <div className="card-header font-weight-bold text-uppercase">{t('addPortfolios.automaticLetterGenerationSettings')}</div>
                    <div className="card-body">
                      <Row>
                        <Col md="3" className="font-weight-bold mb-2">{t('addPortfolios.doYouWantToSendA')}</Col>
                        <Col md="2" className="font-weight-bold mb-2"></Col>
                        <Col md="4" className="font-weight-bold mb-2">{t('addPortfolios.noticeType')}</Col>
                        <Col md="3" className="font-weight-bold mb-2">{t('addPortfolios.numberOfDaysBeforeSending')}</Col>
                      </Row>
                      {formData.letter_generation_settings.map((setting, index) => {
                        // Show days input only for "Auto Reminders/Loan Bills" and "Insurance Reminder Notices"
                        const showDaysInput = setting.name === "Auto Reminders/Loan Bills" || setting.name === "Insurance Reminder Notices";
                        const getLetterSettingDisplayName = (name) => {
                          const map = {
                            "Auto Reminders/Loan Bills": t('addPortfolios.autoRemindersLoanBills'),
                            "10 Day Past Due Notice": t('addPortfolios.10DayPastDueNotice'),
                            "15 Day Past Due Notice": t('addPortfolios.15DayPastDueNotice'),
                            "30 Day Past Due Notice": t('addPortfolios.30DayPastDueNotice'),
                            "60 Day Past Due Notice": t('addPortfolios.60DayPastDueNotice'),
                            "90 Day Past Due Notice": t('addPortfolios.90DayPastDueNotice'),
                            "120 Day Past Due Notice": t('addPortfolios.120DayPastDueNotice'),
                            "Insurance Reminder Notices": t('addPortfolios.insuranceReminderNotices'),
                            "Expired Insurance Notices": t('addPortfolios.expiredInsuranceNotices'),
                          };
                          return map[name] || name;
                        };
                        return (
                          <Row key={index} className="mb-2">
                            <Col md="3">
                              <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                                <span>{getLetterSettingDisplayName(setting.name)}</span>
                              </div>
                            </Col>
                            <Col md="2">
                              <Select
                                styles={customSelectStyles}
                                className="react-select"
                                placeholder={t('common.select')}
                                name={`send_${index}`}
                                isSearchable={false}
                                closeMenuOnSelect={true}
                                value={yesNoOptions.find(opt => opt.value === setting.send)}
                                onChange={(value) => handleLetterSettingChange(index, 'send', value?.value ?? false)}
                                options={yesNoOptions}
                              />
                            </Col>
                            <Col md="4">
                              <Select
                                styles={customSelectStyles}
                                className="react-select"
                                placeholder={t('common.select')}
                                name={`notice_type_${index}`}
                                isSearchable
                                closeMenuOnSelect={true}
                                value={setting.notice_type ? { value: setting.notice_type, label: setting.notice_type } : null}
                                onChange={(value) => handleLetterSettingChange(index, 'notice_type', value?.value || "")}
                                options={[]}
                                isClearable
                              />
                            </Col>
                            <Col md="3">
                              {showDaysInput && (
                                <Input
                                  type="number"
                                  value={setting.days_before}
                                  onChange={(e) => handleLetterSettingChange(index, 'days_before', parseInt(e.target.value) || 0)}
                                  min="0"
                                />
                              )}
                            </Col>
                          </Row>
                        );
                      })}
                    </div>
                  </div>
                </Row>

                <Row>
                  <Col md="12">
                    <div className="d-flex justify-content-end gap-2 mt-4">
                      <Button
                        color="secondary"
                        onClick={handleCancel}
                        disabled={createMutation.isPending}
                        className="px-4"
                      >
                        {t('common.cancel')}
                      </Button>
                      <Button
                        style={{ ...activeColorStyle, color: 'white' }}
                        type="submit"
                        disabled={createMutation.isPending}
                        className="px-4"
                      >
                        {createMutation.isPending ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            {t('addPortfolios.creating')}
                          </>
                        ) : (
                          <>
                            <i className="tim-icons icon-simple-add" style={{ marginRight: '8px' }} />
                            {t('addPortfolios.createPortfolio')}
                          </>
                        )}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AddPortfolios;


